import React, { useState, useMemo } from "react";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import WarningIcon from "@mui/icons-material/Warning";
import BalanceIcon from "@mui/icons-material/Balance";
import { StatCard, LineChart, DonutChart, CombinedBarLineChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { stressStats, stressTrends, stressDistribution, stressCombinedPatterns } from "../data";

export const StressMind: React.FC = () => {
  const theme = useTheme();
  const [trendView, setTrendView] = useState("stress_score");

  const distributionData = useMemo(() => [
    { ...stressDistribution[0], color: theme.palette.success.main },
    { ...stressDistribution[1], color: theme.palette.warning.main },
    { ...stressDistribution[2], color: theme.palette.error.main },
  ], [theme]);

  const statCards = [
    { metric: stressStats.stress_score, icon: SelfImprovementIcon },
    { metric: stressStats.hrv, icon: MonitorHeartIcon },
    { metric: stressStats.high_stress_days, icon: WarningIcon },
    { metric: stressStats.stress_recovery_balance, icon: BalanceIcon },
  ];

  const getTrendLabel = () => {
    switch (trendView) {
      case "stress_score": return "Stress Score";
      case "hrv": return "HRV";
      case "stress_recovery_balance": return "Stress-Recovery Balance";
      default: return "Stress Score";
    }
  };

  const getTrendColor = () => {
    switch (trendView) {
      case "stress_score": return theme.palette.warning.main;
      case "hrv": return theme.palette.info.main;
      case "stress_recovery_balance": return theme.palette.success.main;
      default: return theme.palette.warning.main;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Physiological Stress Trends"
              subtitle="Daily stress and mental wellbeing metrics."
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, v) => { if (v !== null) setTrendView(v); }}
                  size="small"
                >
                  <ToggleButton value="stress_score">Stress Score</ToggleButton>
                  <ToggleButton value="hrv">HRV</ToggleButton>
                  <ToggleButton value="stress_recovery_balance">Recovery Balance</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <LineChart
                data={stressTrends}
                xAxisKey="label"
                series={[{ dataKey: trendView, label: getTrendLabel(), color: getTrendColor(), area: true }]}
                height={300}
                yAxisMin={0}
                yAxisMax={trendView === "stress_score" ? 5 : 100}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Stress Distribution"
              subtitle="Proportion of employees at each stress level."
            >
              <DonutChart data={distributionData} height={300} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Stress Score vs HRV by Day"
              subtitle="Weekly comparison of stress levels and heart rate variability."
            >
              <CombinedBarLineChart
                data={stressCombinedPatterns}
                xAxisKey="day"
                barSeries={[{ dataKey: "stress_score", label: "Stress Score", color: theme.palette.warning.main }]}
                lineSeries={[{ dataKey: "hrv", label: "HRV (ms)", color: theme.palette.info.main }]}
                height={300}
                barYAxisMin={0}
                barYAxisMax={5}
                lineYAxisMin={0}
                lineYAxisMax={100}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
